import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Heart, Sparkles, Play, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { backendClient } from '@/lib/backendClient';
import { getAuthRedirectUrl } from '@/utils/canonicalUrl';
import { getReferralCodeFromUrl } from '@/utils/referralUtils';
import { ReferralCodeInput } from '@/components/user/ReferralCodeInput';

const logoUrl = '/lovable-uploads/voicethera-logo-new.png';



const Auth = () => {
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset' | 'recovery'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralCodeId, setReferralCodeId] = useState<string | null>(null);

  // OTP verification state
  const [signupStep, setSignupStep] = useState<'form' | 'verify'>('form');
  const [pendingEmail, setPendingEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [otpError, setOtpError] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  // Validation state
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string>('');
  const [touched, setTouched] = useState({ email: false, password: false });

  const { signIn, signUp, user, setAuthState } = useAuth();
  const { setDemoMode } = useDemo();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check for recovery mode, referral codes, and handle redirects
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const type = urlParams.get('type');
  const code = urlParams.get('code');
  
  // Handle OAuth success from backend redirect
  const oauthSuccess = urlParams.get('oauth_success');
  const redirectTo = urlParams.get('redirect_to');

  if (oauthSuccess === 'true') {
    console.log('OAuth success, fetching user data with cookies');

    // Fetch user data using cookie authentication
    fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
      method: 'GET',
      credentials: 'include', // Include cookies
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }
      return response.json();
    })
    .then(userData => {
      // Store user data and auth flag
      localStorage.setItem('user_data', JSON.stringify(userData));
      localStorage.setItem('is_authenticated', 'true');

      console.log('✅ User authenticated successfully after OAuth redirect:', userData);

      // Use full page redirect to ensure AuthContext re-initializes with new data
      window.location.href = redirectTo || '/app';
    })
    .catch(error => {
      console.error('Failed to fetch user data after OAuth:', error);
      toast({
        title: "Authentication Failed",
        description: "Could not complete sign in. Please try again.",
        variant: "destructive",
      });

      // Clean URL anyway
      window.history.replaceState({}, document.title, '/auth');
    });

    return; // Stop processing other auth logic
  }

  
  // Extract and store referral code if present
  const refCode = getReferralCodeFromUrl();
  if (refCode) {
    setReferralCode(refCode);
    toast({
      title: "Referral Link Detected",
      description: "Sign up to be connected with your referring practitioner!",
    });
  }
  
  // Handle password recovery mode
  if (type === 'recovery' && code) {
    console.log('Handling password recovery with code');
    setMode('recovery');
    
    backendClient.auth.exchangeCodeForSession(code).then(({ data, error }) => {
      if (error) {
        console.error('Code exchange error:', error);
        toast({
          title: "Password Reset Link Invalid",
          description: "This password reset link has expired or is invalid. Please request a new one.",
          variant: "destructive",
        });
        setMode('signin');
      } else {
        console.log('Code exchange successful, user can now set new password');
        toast({
          title: "Ready to Reset",
          description: "Please enter your new password below.",
        });
        window.history.replaceState({}, document.title, '/auth');
      }
    });
    return;
  }
  
  if (type === 'recovery') {
    toast({
      title: "Invalid Recovery Link",
      description: "This password reset link is incomplete. Please request a new one.",
      variant: "destructive",
    });
    setMode('signin');
    return;
  }
  
  // Check for backend authentication
  if (import.meta.env.VITE_USE_BACKEND === 'true') {
    // Check if user is already authenticated via cookies
    const isAuthenticated = localStorage.getItem('is_authenticated');
    if (isAuthenticated === 'true') {
      // Validate that the cookie is still valid
      fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Session invalid');
        }
        return response.json();
      })
      .then(userData => {
        localStorage.setItem('user_data', JSON.stringify(userData));
        if (window.location.pathname === '/auth') {
          // Check if there's a referral code in the URL
          const refCode = getReferralCodeFromUrl();
          if (refCode) {
            // Redirect to apply-referral page with the referral code
            navigate(`/apply-referral?ref=${refCode}`, { replace: true });
          } else {
            navigate('/app', { replace: true });
          }
        }
      })
      .catch(() => {
        console.log('Session is invalid');
        localStorage.removeItem('is_authenticated');
        localStorage.removeItem('user_data');
      });
    }
  } else if (user) {
    // Using Supabase auth and user is authenticated
    console.log('User is authenticated via Supabase, redirecting to app');
    // Check if there's a referral code in the URL
    const refCode = getReferralCodeFromUrl();
    if (refCode) {
      // Redirect to apply-referral page with the referral code
      navigate(`/apply-referral?ref=${refCode}`, { replace: true });
    } else {
      navigate('/app', { replace: true });
    }
  }
}, [user, navigate, toast]);


  // OTP expiry countdown timer
  useEffect(() => {
    if (otpExpiry > 0) {
      const timer = setTimeout(() => setOtpExpiry(otpExpiry - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpExpiry]);

  // OTP resend cooldown timer
  useEffect(() => {
    if (otpResendCooldown > 0) {
      const timer = setTimeout(() => setOtpResendCooldown(otpResendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendCooldown]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    }
    return errors;
  };

  // Real-time validation
  useEffect(() => {
    if (mode === 'signup' && touched.password && password) {
      setPasswordErrors(validatePassword(password));
    }
  }, [password, mode, touched.password]);

  useEffect(() => {
    if (touched.email && email) {
      validateEmail(email);
    }
  }, [email, touched.email]);

  const handleDemoMode = () => {
    setDemoMode(true);
    toast({
      title: "Demo Mode Activated!",
      description: "Explore the meditation app with full access. Create an account anytime to save your progress.",
    });
    navigate('/app', { replace: true });
  };

const handleGoogleSignIn = async () => {
  setGoogleLoading(true);
  try {
    if (import.meta.env.VITE_USE_BACKEND === 'true') {
      // Backend OAuth - include referral code in state parameter
      const state = referralCode ? `/app?ref=${encodeURIComponent(referralCode)}` : '/app';
      window.location.href = `${import.meta.env.VITE_BACKEND_URL}/auth/google?state=${encodeURIComponent(state)}`;
    } else {
      // Supabase OAuth fallback
      const { error } = await backendClient.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl('/app'),
        }
      });

      if (error) {
        console.error('Google sign-in error:', error);
        toast({
          title: "Google Sign-In Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  } catch (error: any) {
    console.error('Google sign-in exception:', error);
    toast({
      title: "Error",
      description: error.message || "An unexpected error occurred with Google sign-in",
      variant: "destructive",
    });
  } finally {
    setGoogleLoading(false);
  }
};

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address first.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await backendClient.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthRedirectUrl('/auth?type=recovery'),
      });

      if (error) {
        toast({
          title: "Password Reset Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Password Reset Email Sent",
          description: "Check your email for a password reset link.",
        });
        setMode('signin');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) return;

    setLoading(true);
    try {
      const { error } = await backendClient.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast({
          title: "Password Update Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Password Updated",
          description: "Your password has been successfully updated.",
        });
        setMode('signin');
        setNewPassword('');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP signup initiation (Step 1)
  const handleInitiateSignup = async () => {
    // Frontend validation
    const isEmailValid = validateEmail(email);
    const passErrors = validatePassword(password);

    if (!isEmailValid || passErrors.length > 0) {
      setPasswordErrors(passErrors);
      setTouched({ email: true, password: true });
      toast({
        title: "Validation Error",
        description: "Please fix the errors before continuing.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await backendClient.auth.initiateSignUp({
        email,
        password,
        options: {
          data: {
            first_name: fullName.split(' ')[0] || '',
            last_name: fullName.split(' ').slice(1).join(' ') || '',
            full_name: fullName,
            referral_code_id: referralCodeId,
          }
        }
      });

      if (error) {
        toast({
          title: "Sign Up Failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // Success - move to OTP verification step
      setPendingEmail(email);
      setOtpExpiry(data?.expires_in || 600);
      setOtpResendCooldown(60);
      setSignupStep('verify');
      setOtpValue('');
      setOtpError('');

      toast({
        title: "Verification Code Sent",
        description: `Please check your email at ${email} for a 6-digit verification code.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification (Step 2)
  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      setOtpError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setOtpError('');

    try {
      const { data, error } = await backendClient.auth.verifySignUpOTP(pendingEmail, otpValue);

      if (error) {
        setOtpError(error.message);
        toast({
          title: "Verification Failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // Success - user is now logged in
      localStorage.setItem('is_authenticated', 'true');
      if (data?.user) {
        localStorage.setItem('user_data', JSON.stringify(data.user));
        // Update AuthContext state so sidebar and other components reflect the logged-in state immediately
        setAuthState(data.user);
      }

      toast({
        title: "Account Created!",
        description: "Your email has been verified and your account is ready.",
      });

      // Navigate to app
      navigate('/app', { replace: true });
    } catch (error: any) {
      setOtpError(error.message || 'Verification failed');
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP resend
  const handleResendOtp = async () => {
    if (otpResendCooldown > 0) return;

    setLoading(true);
    try {
      const { data, error } = await backendClient.auth.resendSignUpOTP(pendingEmail);

      if (error) {
        toast({
          title: "Resend Failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setOtpExpiry(data?.expires_in || 600);
      setOtpResendCooldown(60);
      setOtpValue('');
      setOtpError('');

      toast({
        title: "New Code Sent",
        description: "A new verification code has been sent to your email.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle going back to signup form
  const handleBackToSignupForm = () => {
    setSignupStep('form');
    setPendingEmail('');
    setOtpValue('');
    setOtpError('');
    setOtpExpiry(0);
  };

  // Format time for countdown display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'recovery') {
      return handlePasswordRecovery(e);
    }

    // Frontend validation for signup
    if (mode === 'signup') {
      const isEmailValid = validateEmail(email);
      const passErrors = validatePassword(password);

      if (!isEmailValid || passErrors.length > 0) {
        setPasswordErrors(passErrors);
        setTouched({ email: true, password: true });
        toast({
          title: "Validation Error",
          description: "Please fix the errors before continuing.",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'signin') {
        console.log('Attempting login for:', email);
        const { error } = await signIn(email, password);
        if (error) {
          console.error('Login error:', error);
          let errorMessage = "We couldn't sign you in. Please check your details and try again.";

          if (error.message.includes('Invalid login credentials')) {
            errorMessage = "Hmm, something's not quite right. This could mean:\n• Your password needs updating\n• Your email hasn't been verified yet\n\nTry 'Forgot Password' or 'Resend Verification' below to get back on track.";
          } else if (error.message.includes('Email not confirmed')) {
            errorMessage = "Almost there! Please check your email and click the verification link first. Don't see it? Check your spam folder or use 'Resend Verification' below.";
          } else if (error.message.includes('Too many requests')) {
            errorMessage = "You're trying to sign in too quickly. Please wait a moment and try again.";
          }

          toast({
            title: "Login Failed",
            description: errorMessage,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Welcome back!",
            description: "You've successfully logged in.",
          });
        }
      } else if (mode === 'signup') {
        // Use new OTP signup flow
        await handleInitiateSignup();
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={logoUrl} alt="Meditation App" className="h-16 w-16" />
          </div>
          <h1 className="text-3xl font-light text-foreground">
            Meditation App
          </h1>
          <p className="text-muted-foreground mt-2 font-light">
            Personalized AI meditation and wellness
          </p>
        </div>

        {/* Demo Mode CTA */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-6 text-white">
          <div className="text-center">
            <Play className="h-12 w-12 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Experience the App</h3>
            <p className="text-green-100 mb-4 text-sm">
              Explore personalized meditations, anxiety relief, sleep sessions, and more - no signup required!
            </p>
            <Button
              onClick={handleDemoMode}
              className="bg-white text-green-600 hover:bg-green-50 font-semibold px-6 py-2 rounded-xl"
            >
              Start Demo Now
            </Button>
          </div>
        </div>

        {/* Auth form */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/20">
          {/* OTP Verification Step */}
          {mode === 'signup' && signupStep === 'verify' ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Verify Your Email
                </h2>
                <p className="text-gray-600 text-sm">
                  We sent a 6-digit code to <span className="font-medium">{pendingEmail}</span>
                </p>
              </div>

              {/* OTP Expiry Timer */}
              {otpExpiry > 0 && (
                <div className="text-center">
                  <span className={`text-sm ${otpExpiry < 60 ? 'text-red-600' : 'text-gray-500'}`}>
                    Code expires in {formatTime(otpExpiry)}
                  </span>
                </div>
              )}

              {/* OTP Input */}
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm font-medium text-gray-700">
                  Verification Code
                </Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={otpValue}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpValue(value);
                    setOtpError('');
                  }}
                  className={`h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500 ${
                    otpError ? 'border-red-500' : ''
                  }`}
                />
                {otpError && (
                  <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{otpError}</span>
                  </div>
                )}
              </div>

              {/* Verify Button */}
              <Button
                onClick={handleVerifyOtp}
                disabled={loading || otpValue.length !== 6}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium transition-all duration-200"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>

              {/* Resend and Back buttons */}
              <div className="flex flex-col space-y-2 text-center">
                <button
                  onClick={handleResendOtp}
                  disabled={loading || otpResendCooldown > 0}
                  className="text-sm text-purple-600 hover:text-purple-700 transition-colors disabled:text-gray-400"
                >
                  {otpResendCooldown > 0
                    ? `Resend code (${otpResendCooldown}s)`
                    : "Didn't receive the code? Resend"}
                </button>
                <button
                  onClick={handleBackToSignupForm}
                  className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
                >
                  Use a different email
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center mb-6">
                <Sparkles className="h-5 w-5 text-purple-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  {mode === 'signin' && 'Welcome Back'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'reset' && 'Reset Password'}
                  {mode === 'recovery' && 'Set New Password'}
                </h2>
              </div>

              {/* Google Sign-In Button */}
          <Button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full h-12 rounded-xl mb-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium transition-all duration-200 transform hover:scale-[1.02]"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? 'Signing in with Google...' : `Continue with Google`}
          </Button>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                    Full Name
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Sarah Johnson"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>

                {/* Referral Code Input */}
                <ReferralCodeInput
                  value={referralCode}
                  onChange={setReferralCode}
                  onValidationChange={(isValid, codeId) => {
                    if (isValid && codeId) {
                      setReferralCodeId(codeId);
                    } else {
                      setReferralCodeId(null);
                    }
                  }}
                  initialValue={referralCode}
                  placeholder="Have a referral code? Enter it here"
                  showLabel={true}
                />
              </>
            )}

            {mode !== 'recovery' && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched({ ...touched, email: true })}
                  required
                  className={`h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500 ${
                    emailError && touched.email ? 'border-red-500' : ''
                  }`}
                />
                {emailError && touched.email && (
                  <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{emailError}</span>
                  </div>
                )}
              </div>
            )}

            {mode === 'recovery' ? (
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={mode === 'signup' ? 'At least 8 characters with a digit' : 'Your secure password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched({ ...touched, password: true })}
                    required
                    className={`h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500 pr-12 ${
                      passwordErrors.length > 0 && touched.password && mode === 'signup' ? 'border-red-500' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Password requirements hint for signup */}
                {mode === 'signup' && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-gray-600 font-medium">Password requirements:</p>
                    <div className="flex items-center gap-1 text-xs">
                      {password.length >= 8 ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span className={password.length >= 8 ? 'text-green-600' : 'text-gray-600'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      {/\d/.test(password) ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span className={/\d/.test(password) ? 'text-green-600' : 'text-gray-600'}>
                        Contains at least one digit (0-9)
                      </span>
                    </div>
                  </div>
                )}

                {/* Password errors */}
                {passwordErrors.length > 0 && touched.password && mode === 'signup' && (
                  <div className="space-y-1 mt-2">
                    {passwordErrors.map((error, index) => (
                      <div key={index} className="flex items-center gap-1 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium transition-all duration-200 transform hover:scale-[1.02]"
            >
              {loading ? 'Please wait...' : 
               mode === 'signin' ? 'Sign In' : 
               mode === 'signup' ? 'Create Account' :
               mode === 'recovery' ? 'Update Password' : 'Submit'}
            </Button>
          </form>

          {/* Auth helper links */}
          {mode === 'signin' && (
            <div className="mt-4 text-center">
              <button
                onClick={handleForgotPassword}
                disabled={loading}
                className="text-sm text-purple-600 hover:text-purple-700 transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            {mode === 'recovery' ? (
              <button
                onClick={() => setMode('signin')}
                className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
              >
                Back to sign in
              </button>
            ) : (
              <button
                onClick={() => {
                  setMode(mode === 'signin' ? 'signup' : 'signin');
                  setSignupStep('form'); // Reset signup step when switching modes
                }}
                className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
              >
                {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            )}
          </div>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
